// ============================================================================
// STORE.FIREBASE.JS - Firebase/Firestore Implementation
//
// Data Model:
//   tenants/{tenantId}
//   tenants/{tenantId}/specialties/{specialtyId}
//   tenants/{tenantId}/specialties/{specialtyId}/packs/{packId}__{versionId}
//   tenants/{tenantId}/metrics/{metricId}
//   users/{uid}
//   seed_templates/specialties/{specialtyId}
//   seed_templates/packs/{specialtyId}__{packId}__{versionId}
//
// CRITICAL: Metrics can only contain whitelisted non-PHI fields
// ============================================================================

import { initializeApp, getApps } from 'firebase/app';
import { PATHS } from './firestorePaths.js';
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter as startAfterDoc,
  serverTimestamp,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { initializeAppCheckForApp } from './appCheck.js';

// ============================================================================
// FIREBASE INITIALIZATION
// ============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app = null;
let db = null;

const initFirebase = () => {
  if (!app && firebaseConfig.apiKey) {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    // App Check (S2): attest this app instance before any sensitive callable
    // runs. No-op unless VITE_RECAPTCHA_SITE_KEY is set; idempotent across the
    // auth.firebase.js / store.firebase.js bootstrap paths.
    initializeAppCheckForApp(app);
    db = getFirestore(app);
  }
  return db;
};

/**
 * Get the Firebase app instance (for use with other Firebase services)
 */
export const getFirebaseApp = () => {
  if (!app && firebaseConfig.apiKey) {
    initFirebase();
  }
  return app;
};

const getDb = () => {
  if (!db) initFirebase();
  if (!db) throw new Error('Firebase not initialized. Check environment variables.');
  return db;
};

const getCurrentUserId = () => {
  const auth = getAuth();
  return auth.currentUser?.uid || 'system';
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const toTimestamp = () => serverTimestamp();

/**
 * Convert Firestore Timestamp to milliseconds (for date filtering)
 * Supports multiple Firestore Timestamp formats
 */
const timestampToMs = (ts) => {
  if (!ts) return null;

  // Firestore Timestamp object
  if (ts instanceof Timestamp) {
    return ts.toMillis();
  }

  // Firestore Timestamp-like object with toMillis
  if (typeof ts.toMillis === 'function') {
    return ts.toMillis();
  }

  // Firestore Timestamp-like object with toDate
  if (typeof ts.toDate === 'function') {
    return ts.toDate().getTime();
  }

  // Firestore Timestamp-like object with seconds
  if (ts.seconds !== undefined) {
    return ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000;
  }

  // Already a number (ms)
  if (typeof ts === 'number') {
    return ts;
  }

  // ISO string
  if (typeof ts === 'string') {
    return new Date(ts).getTime();
  }

  // Date object
  if (ts instanceof Date) {
    return ts.getTime();
  }

  return null;
};

/**
 * Convert Firestore Timestamp to ISO string
 */
const fromTimestamp = (ts) => {
  if (!ts) return null;
  if (ts instanceof Timestamp) return ts.toDate().toISOString();
  if (ts.toDate) return ts.toDate().toISOString();
  return ts;
};

/**
 * Clean a Firestore document snapshot
 * Converts timestamps and adds document ID
 * IMPORTANT: This must NEVER return null unless docSnap.exists() is false
 */
const cleanDoc = (docSnap) => {
  if (!docSnap.exists()) {
    console.warn('[cleanDoc] Document does not exist:', docSnap.id);
    return null;
  }

  const data = docSnap.data();

  if (!data) {
    console.warn('[cleanDoc] Document exists but data() returned null:', docSnap.id);
    return null;
  }

  // Build cleaned document with all original fields preserved
  const cleaned = {
    ...data,
    id: docSnap.id,
  };

  // Convert timestamps if they exist
  if (data.createdAt) {
    cleaned.createdAt = fromTimestamp(data.createdAt);
    cleaned.createdAtMs = timestampToMs(data.createdAt);
  }

  if (data.updatedAt) {
    cleaned.updatedAt = fromTimestamp(data.updatedAt);
    cleaned.updatedAtMs = timestampToMs(data.updatedAt);
  }

  if (data.savedAt) {
    cleaned.savedAt = fromTimestamp(data.savedAt);
    cleaned.savedAtMs = timestampToMs(data.savedAt);
  }

  return cleaned;
};

// ============================================================================
// TENANTS
// Path: tenants/{tenantId}
// ============================================================================

export const getTenants = async () => {
  const db = getDb();
  const tenantsRef = collection(db, PATHS.col.tenants());
  const snapshot = await getDocs(tenantsRef);

  const tenants = {};
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) tenants[docSnap.id] = data;
  });

  return tenants;
};

export const getTenant = async (tenantId) => {
  const db = getDb();
  const tenantRef = doc(db, PATHS.tenant(tenantId));
  const tenantSnap = await getDoc(tenantRef);
  return cleanDoc(tenantSnap);
};

export const saveTenant = async (tenant) => {
  const db = getDb();
  const tenantId = tenant.tenantId || tenant.id;
  const tenantRef = doc(db, PATHS.tenant(tenantId));
  const existing = await getDoc(tenantRef);
  const userId = getCurrentUserId();

  const data = {
    tenantId,
    name: tenant.name,
    region: tenant.region || 'US',
    status: tenant.status || 'active',
    seats: tenant.seats || 10,
    counters: tenant.counters || {},
    updatedAt: toTimestamp(),
    updatedBy: userId,
  };

  if (!existing.exists()) {
    data.createdAt = toTimestamp();
    data.createdBy = userId;
    data.lifecycle = 'pre_launch';
  }

  await setDoc(tenantRef, data, { merge: true });
  return { success: true, tenantId };
};

export const updateTenantLifecycle = async (tenantId, newLifecycle) => {
  const db = getDb();
  const batch = writeBatch(db);
  const userId = getCurrentUserId();

  const tenantRef = doc(db, PATHS.tenant(tenantId));
  const existing = await getDoc(tenantRef);
  if (!existing.exists()) throw new Error(`Tenant ${tenantId} not found`);

  const tenantData = existing.data();
  const previousLifecycle = tenantData.lifecycle || 'pre_launch';

  const tenantUpdate = {
    lifecycle: newLifecycle,
    updatedAt: toTimestamp(),
    updatedBy: userId,
  };

  if (newLifecycle === 'live' && !tenantData.launchedAt) {
    tenantUpdate.launchedAt = toTimestamp();
    tenantUpdate.launchedBy = userId;
  }

  batch.update(tenantRef, tenantUpdate);

  const eventId = `AE-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
  const eventRef = doc(db, PATHS.tenantAdminEvent(tenantId, eventId));
  batch.set(eventRef, {
    eventId,
    type: 'LIFECYCLE_CHANGED',
    from: previousLifecycle,
    to: newLifecycle,
    actorUid: userId,
    tenantId,
    createdAt: toTimestamp(),
  });

  await batch.commit();
  return { success: true, tenantId, lifecycle: newLifecycle, eventId };
};

export const deleteTenant = async (tenantId) => {
  const db = getDb();
  const tenantRef = doc(db, PATHS.tenant(tenantId));
  await deleteDoc(tenantRef);
  return true;
};

export const updateTenantCounters = async (tenantId) => {
  const db = getDb();

  // Count active users
  const usersRef = collection(db, PATHS.col.users());
  const usersQuery = query(usersRef, where('tenantId', '==', tenantId), where('status', '==', 'active'));
  const usersSnap = await getDocs(usersQuery);

  // Count active specialties
  const specialtiesRef = collection(db, PATHS.col.specialties(tenantId));
  const specialtiesQuery = query(specialtiesRef, where('status', '==', 'active'));
  const specialtiesSnap = await getDocs(specialtiesQuery);

  // Count active packs (across all specialties)
  let activePacks = 0;
  for (const specDoc of specialtiesSnap.docs) {
    const packsRef = collection(db, PATHS.col.packs(tenantId, specDoc.id));
    const packsQuery = query(packsRef, where('status', '==', 'published'));
    const packsSnap = await getDocs(packsQuery);
    activePacks += packsSnap.size;
  }

  const tenantRef = doc(db, PATHS.tenant(tenantId));
  await updateDoc(tenantRef, {
    counters: {
      activeUsers: usersSnap.size,
      activeSpecialties: specialtiesSnap.size,
      activePacks,
    },
    updatedAt: toTimestamp(),
  });
};

// ============================================================================
// SPECIALTIES
// Path: tenants/{tenantId}/specialties/{specialtyId}
// ============================================================================

export const getSpecialties = async (tenantId) => {
  const db = getDb();
  const specialtiesRef = collection(db, PATHS.col.specialties(tenantId));
  const q = query(specialtiesRef, where('status', '==', 'active'));
  const snapshot = await getDocs(q);

  const specialties = {};
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) specialties[docSnap.id] = data;
  });

  return specialties;
};

export const getSpecialty = async (tenantId, specialtyId) => {
  const db = getDb();
  const specialtyRef = doc(db, PATHS.specialty(tenantId, specialtyId));
  const specialtySnap = await getDoc(specialtyRef);
  return cleanDoc(specialtySnap);
};

export const saveSpecialty = async (tenantId, specialty) => {
  const db = getDb();
  const specialtyId = specialty.specialtyId || specialty.id;
  const specialtyRef = doc(db, PATHS.specialty(tenantId, specialtyId));
  const existing = await getDoc(specialtyRef);
  const userId = getCurrentUserId();

  const data = {
    specialtyId,
    name: specialty.name,
    description: specialty.description || '',
    icon: specialty.icon || 'Shield',
    status: specialty.status || 'active',
    sortOrder: specialty.sortOrder ?? 0,
    activePack: specialty.activePack || null,
    updatedAt: toTimestamp(),
    updatedBy: userId,
  };

  if (!existing.exists()) {
    data.createdAt = toTimestamp();
    data.createdBy = userId;
  }

  await setDoc(specialtyRef, data, { merge: true });
  return { success: true, specialtyId };
};

export const deleteSpecialty = async (tenantId, specialtyId) => {
  const db = getDb();
  const specialtyRef = doc(db, PATHS.specialty(tenantId, specialtyId));
  await deleteDoc(specialtyRef);
  return true;
};

// ============================================================================
// TENANT SETTINGS
// Path: tenants/{tenantId}/settings/{settingId}
//
// Settings documents:
//   - security: { allowPerCaseAuditRead, allowedAuditSpecialtyIds }
// ============================================================================

export const getTenantSettings = async (tenantId, settingId) => {
  const db = getDb();
  const settingRef = doc(db, PATHS.setting(tenantId, settingId));
  const settingSnap = await getDoc(settingRef);
  return cleanDoc(settingSnap);
};

export const getSecuritySettings = async (tenantId) => {
  return getTenantSettings(tenantId, 'security');
};

export const saveTenantSettings = async (tenantId, settingId, settings) => {
  const db = getDb();
  const settingRef = doc(db, PATHS.setting(tenantId, settingId));
  const userId = getCurrentUserId();

  const data = {
    ...settings,
    updatedAt: toTimestamp(),
    updatedBy: userId,
  };

  await setDoc(settingRef, data, { merge: true });
  return { success: true, settingId };
};

export const saveSecuritySettings = async (tenantId, settings) => {
  // Validate security settings
  const validSettings = {
    allowPerCaseAuditRead: Boolean(settings.allowPerCaseAuditRead),
    allowedAuditSpecialtyIds: Array.isArray(settings.allowedAuditSpecialtyIds)
      ? settings.allowedAuditSpecialtyIds
      : [],
  };

  return saveTenantSettings(tenantId, 'security', validSettings);
};

// ============================================================================
// PACKS (Versioned)
// Path: tenants/{tenantId}/specialties/{specialtyId}/packs/{packId}__{versionId}
// ============================================================================

/**
 * Get a single pack by its document ID
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {string} packDocId - Format: {packId}__{versionId}
 * @returns {Promise<object|null>}
 */
export const getPackByDocId = async (tenantId, specialtyId, packDocId) => {
  const db = getDb();
  const packRef = doc(db, PATHS.pack(tenantId, specialtyId, packDocId));
  const packSnap = await getDoc(packRef);

  if (!packSnap.exists()) {
    console.warn('[getPackByDocId] Pack not found:', packDocId);
    return null;
  }

  const data = cleanDoc(packSnap);

  if (!data) {
    return null;
  }

  // Normalize the pack structure for engine compatibility (same as getPacks)
  // The 'pack' field contains the actual pack JSON with metadata, fields, rules, etc.
  const packContent = data.pack || {};

  // Build complete metadata with fallbacks
  const normalizedMetadata = {
    id: data.packId || packContent.metadata?.id || packDocId.split('__')[0],
    name: data.name || packContent.metadata?.name || 'Unnamed Pack',
    version: data.versionId || packContent.metadata?.version || '1.0.0',
    specialtyId: data.specialtyId || specialtyId || packContent.metadata?.specialtyId,
    specialty: packContent.metadata?.specialty || data.specialtyId || specialtyId,
    intent: data.description || packContent.metadata?.intent || '',
  };

  // Extract fields with fallback
  const fields = packContent.fields || data.fields || [];
  const redFlags = packContent.redFlags || data.redFlags || [];
  const patientMessages = packContent.patientMessages || data.patientMessages || {};

  // Return normalized pack (same structure as getPacks)
  const normalized = {
    // Top-level identifiers
    id: packDocId,
    packDocId: packDocId,
    tenantId: data.tenantId || tenantId,
    specialtyId: data.specialtyId || specialtyId,
    packId: data.packId || packContent.metadata?.id || packDocId.split('__')[0],
    versionId: data.versionId || packContent.metadata?.version || '1.0.0',
    status: data.status || 'active',
    name: data.name || packContent.metadata?.name || 'Unnamed Pack',
    description: data.description || packContent.metadata?.intent || '',
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,

    // IMPORTANT: metadata for engine compatibility
    metadata: normalizedMetadata,

    // IMPORTANT: fields array for clinical form
    fields,

    // IMPORTANT: redFlags array for red flags step
    redFlags,

    // IMPORTANT: patientMessages for output generation
    patientMessages,

    // IMPORTANT: pack content for engine (nested structure expected by evaluateReferral)
    pack: packContent,

    // IMPORTANT: redirect flag arrays — Gate contract requires these before
    // red flags and completeness. Missing here = redirect UI never renders.
    intraSpecialtyRedirectFlags: packContent.intraSpecialtyRedirectFlags || data.intraSpecialtyRedirectFlags || [],
    crossSpecialtyRedirectFlags: packContent.crossSpecialtyRedirectFlags || data.crossSpecialtyRedirectFlags || [],
    // Legacy packs store a single redirectFlags array — normalizePackDoc migrates it
    redirectFlags: packContent.redirectFlags || data.redirectFlags || [],

    // Other pack content
    rules: packContent.rules || data.rules || [],
    referralThresholds: packContent.referralThresholds || data.referralThresholds || {},
    outputs: packContent.outputs || data.outputs || [],
  };

  console.log('[getPackByDocId] Normalized pack:', {
    packDocId,
    packId: normalized.packId,
    versionId: normalized.versionId,
    fieldsCount: fields.length,
    redFlagsCount: redFlags.length,
  });

  return normalized;
};

/**
 * Get packs for a tenant, optionally filtered by specialty
 * Only returns active packs for GP/Specialist use
 *
 * @param {string} tenantId
 * @param {string} specialtyId - Optional, if null loads from all specialties
 * @param {object} options - { includeInactive: false }
 * @returns {Promise<object>} - Map of packDocId -> pack data
 */
export const getPacks = async (tenantId, specialtyId = null, options = {}) => {
  const db = getDb();
  const packs = {};
  const includeInactive = options.includeInactive || false;

  const processPacks = (snapshot, specId) => {
    snapshot.forEach((docSnap) => {
      const data = cleanDoc(docSnap);

      if (data) {
        // Filter by status unless includeInactive is true
        if (!includeInactive && data.status !== 'active') {
          return;
        }

        // Normalize the pack structure for GateView compatibility
        // The 'pack' field contains the actual pack JSON with metadata, fields, rules, etc.
        const packContent = data.pack || {};

        // Build complete metadata with fallbacks
        const normalizedMetadata = {
          id: data.packId || packContent.metadata?.id || docSnap.id.split('__')[0],
          name: data.name || packContent.metadata?.name || 'Unnamed Pack',
          version: data.versionId || packContent.metadata?.version || '1.0.0',
          specialtyId: data.specialtyId || specId || packContent.metadata?.specialtyId,
          specialty: packContent.metadata?.specialty || data.specialtyId || specId,
          intent: data.description || packContent.metadata?.intent || '',
        };

        // Extract fields with fallback
        const fields = packContent.fields || data.fields || [];
        const redFlags = packContent.redFlags || data.redFlags || [];
        const patientMessages = packContent.patientMessages || data.patientMessages || {};

        packs[docSnap.id] = {
          // Top-level identifiers
          id: docSnap.id,
          packDocId: docSnap.id,
          tenantId: data.tenantId || tenantId,
          specialtyId: data.specialtyId || specId,
          packId: data.packId || packContent.metadata?.id || docSnap.id.split('__')[0],
          versionId: data.versionId || packContent.metadata?.version || '1.0.0',
          status: data.status || 'active',
          name: data.name || packContent.metadata?.name || 'Unnamed Pack',
          description: data.description || packContent.metadata?.intent || '',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,

          // IMPORTANT: metadata for engine compatibility
          metadata: normalizedMetadata,

          // IMPORTANT: raw pack content — required so normalizePackDoc can read all
          // nested fields (redirect flags, escalation options, not-yet steps, etc.)
          pack: packContent,

          // IMPORTANT: fields array for clinical form
          fields,

          // IMPORTANT: redFlags array for red flags step
          redFlags,

          // IMPORTANT: patientMessages for output generation
          patientMessages,

          // IMPORTANT: redirect flag arrays — Gate contract requires these before
          // red flags and completeness. Missing here = redirect UI never renders.
          intraSpecialtyRedirectFlags: packContent.intraSpecialtyRedirectFlags || data.intraSpecialtyRedirectFlags || [],
          crossSpecialtyRedirectFlags: packContent.crossSpecialtyRedirectFlags || data.crossSpecialtyRedirectFlags || [],
          // Legacy packs store a single redirectFlags array — normalizePackDoc migrates it
          redirectFlags: packContent.redirectFlags || data.redirectFlags || [],

          // Attachment requirements
          attachmentRequirements: packContent.attachmentRequirements || data.attachmentRequirements || [],

          // Other pack content
          rules: packContent.rules || data.rules || [],
          referralThresholds: packContent.referralThresholds || data.referralThresholds || {},
          outputs: packContent.outputs || data.outputs || [],
        };

      }
    });
  };

  try {
    if (specialtyId) {
      // Get packs for specific specialty
      const packsRef = collection(db, PATHS.col.packs(tenantId, specialtyId));
      const snapshot = await getDocs(packsRef);
      processPacks(snapshot, specialtyId);
    } else {
      // Get packs for all specialties
      const specialties = await getSpecialties(tenantId);

      for (const specId of Object.keys(specialties || {})) {
        const packsRef = collection(db, PATHS.col.packs(tenantId, specId));
        const snapshot = await getDocs(packsRef);
        processPacks(snapshot, specId);
      }
    }
  } catch (error) {
    console.error('Error loading packs:', error);
    throw error;
  }

  return packs;
};

export const getPackVersions = async (tenantId, specialtyId, packId) => {
  const db = getDb();
  const packsRef = collection(db, PATHS.col.packs(tenantId, specialtyId));
  const snapshot = await getDocs(packsRef);

  const versions = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data && data.packId === packId) {
      versions.push({
        versionId: data.versionId,
        status: data.status,
        createdAt: data.createdAt,
        createdBy: data.createdBy,
      });
    }
  });

  return versions.sort((a, b) => {
    // Sort by version number descending
    const [aMajor, aMinor, aPatch] = a.versionId.split('.').map(Number);
    const [bMajor, bMinor, bPatch] = b.versionId.split('.').map(Number);
    if (bMajor !== aMajor) return bMajor - aMajor;
    if (bMinor !== aMinor) return bMinor - aMinor;
    return bPatch - aPatch;
  });
};

export const savePackVersion = async (tenantId, specialtyId, packData) => {
  const db = getDb();
  const packId = packData.packId || packData.metadata?.id;
  const versionId = packData.versionId || packData.metadata?.version || '1.0.0';
  const docId = `${packId}__${versionId}`;
  const packRef = doc(db, PATHS.pack(tenantId, specialtyId, docId));
  const userId = getCurrentUserId();

  // Default to 'active' for immediate visibility in pilots
  const status = packData.status || 'active';

  const data = {
    tenantId,
    specialtyId,
    packId,
    versionId,
    packDocId: docId, // Store the doc ID for easy reference
    name: packData.name || packData.metadata?.name || packId,
    status,
    tags: packData.tags || [],
    description: packData.description || packData.metadata?.intent || '',
    schemaVersion: packData.schemaVersion || 1,
    pack: packData.pack || packData, // The full pack JSON
    createdAt: toTimestamp(),
    createdBy: userId,
    updatedAt: toTimestamp(),
    updatedBy: userId,
  };

  await setDoc(packRef, data);
  return { success: true, docId, packId, versionId };
};

export const updatePackStatus = async (tenantId, specialtyId, packId, versionId, status) => {
  const db = getDb();
  const docId = `${packId}__${versionId}`;
  const packRef = doc(db, PATHS.pack(tenantId, specialtyId, docId));

  await updateDoc(packRef, {
    status,
    updatedAt: toTimestamp(),
    updatedBy: getCurrentUserId(),
  });

  return { success: true };
};

/**
 * Edit-in-place: update an existing pack doc without changing its ID, tenant, or specialty.
 * Only the pack content (pack JSON), name, and description are overwritten.
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {string} packDocId  - Format: {packId}__{versionId}  (must match the existing doc)
 * @param {object} normalizedPack - The validated/normalized pack JSON (from validatePack)
 */
export const updatePackDoc = async (tenantId, specialtyId, packDocId, normalizedPack) => {
  const db = getDb();
  const packRef = doc(db, PATHS.pack(tenantId, specialtyId, packDocId));

  await updateDoc(packRef, {
    name: normalizedPack.metadata?.name || normalizedPack.name || packDocId.split('__')[0],
    description: normalizedPack.metadata?.intent || normalizedPack.description || '',
    pack: normalizedPack,
    updatedAt: toTimestamp(),
    updatedBy: getCurrentUserId(),
  });

  return { success: true };
};

export const setActivePack = async (tenantId, specialtyId, packId, versionId) => {
  const db = getDb();
  const specialtyRef = doc(db, PATHS.specialty(tenantId, specialtyId));
  const packDocId = `${packId}__${versionId}`;

  await updateDoc(specialtyRef, {
    activePack: {
      packId,
      versionId,
      packDocId, // Store the doc ID for direct lookup
    },
    updatedAt: toTimestamp(),
    updatedBy: getCurrentUserId(),
  });

  return { success: true };
};

export const deletePack = async (tenantId, specialtyId, packId, versionId) => {
  const db = getDb();
  const docId = `${packId}__${versionId}`;
  const packRef = doc(db, PATHS.pack(tenantId, specialtyId, docId));
  await deleteDoc(packRef);
  return true;
};

// Helper: Check if pack is custom (not from seed)
export const isCustomPack = async (tenantId, specialtyId, packId) => {
  const db = getDb();
  const packsRef = collection(db, PATHS.col.packs(tenantId, specialtyId));
  const snapshot = await getDocs(packsRef);

  for (const docSnap of snapshot.docs) {
    const data = docSnap.data();
    if (data.packId === packId) {
      return true;
    }
  }
  return false;
};

// ============================================================================
// USERS
// Path: users/{uid}
// ============================================================================

export const getUsers = async () => {
  const db = getDb();
  const usersRef = collection(db, PATHS.col.users());
  const snapshot = await getDocs(usersRef);

  const users = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) users.push(data);
  });

  return users;
};

export const getTenantUsers = async (tenantId) => {
  const db = getDb();
  const usersRef = collection(db, PATHS.col.users());
  const q = query(usersRef, where('tenantId', '==', tenantId));
  const snapshot = await getDocs(q);

  const users = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) users.push(data);
  });

  return users;
};

export const getUser = async (userId) => {
  const db = getDb();
  const userRef = doc(db, PATHS.user(userId));
  const userSnap = await getDoc(userRef);
  return cleanDoc(userSnap);
};

/**
 * Get a single user profile by UID, scoped to a tenant.
 * Reads from /users/{uid} (the canonical path) and verifies tenantId matches.
 * This is safer than an unscoped getUser because it confirms the target user
 * belongs to the caller's tenant before returning data.
 *
 * @param {string} tenantId - The tenant the caller belongs to
 * @param {string} uid - The user ID to look up
 * @returns {Promise<Object|null>} User profile or null
 */
export const getTenantUser = async (tenantId, uid) => {
  if (!tenantId || !uid) return null;
  const db = getDb();
  const userRef = doc(db, PATHS.user(uid));
  try {
    const userSnap = await getDoc(userRef);
    const user = cleanDoc(userSnap);
    // Only return if user belongs to the requested tenant
    if (user && user.tenantId === tenantId) return user;
    return null;
  } catch (err) {
    // Permission denied or network error — caller will use UID fallback
    console.warn('[getTenantUser] Could not read user', uid, ':', err.code || err.message);
    return null;
  }
};

/**
 * Save or update a user profile
 *
 * User profile fields:
 *   - uid, email, name (basic)
 *   - role: platform_admin | tenant_admin | specialist | gp_employee
 *   - tenantId: string | null
 *   - specialtyIds: string[] (for specialists, used for analytics scoping)
 *   - status: active | disabled
 *   - createdAt, updatedAt, createdBy, updatedBy
 *
 * NOTE: For new user creation, use the Cloud Function instead.
 * This function is for updates only.
 */
export const saveUser = async (user) => {
  const db = getDb();
  const userId = user.id || user.uid;
  const userRef = doc(db, PATHS.user(userId));
  const existing = await getDoc(userRef);
  const currentUserId = getCurrentUserId();

  // Convert specialtyId (legacy) to specialtyIds array if needed
  let specialtyIds = user.specialtyIds || [];
  if (user.specialtyId && !Array.isArray(user.specialtyIds)) {
    specialtyIds = [user.specialtyId];
  }

  const resolvedStatus = user.status || 'active';
  const data = {
    email: user.email,
    name: user.name || user.displayName || user.email?.split('@')[0],
    role: user.role,
    tenantId: user.tenantId || null,
    specialtyIds: specialtyIds, // Array for multi-specialty support
    status:   resolvedStatus,
    isActive: resolvedStatus === 'active', // Keep in sync with Firestore rules (isActiveUser check)
    updatedAt: toTimestamp(),
    updatedBy: currentUserId,
  };

  if (!existing.exists()) {
    data.uid = userId;
    data.createdAt = toTimestamp();
    data.createdBy = currentUserId;
  }

  await setDoc(userRef, data, { merge: true });
  return { success: true, userId };
};

/**
 * Update only the status field for a user (used by tenant_admin)
 * This is the only field tenant_admin can modify
 */
export const updateUserStatus = async (userId, status) => {
  const db = getDb();
  const userRef = doc(db, PATHS.user(userId));
  const currentUserId = getCurrentUserId();

  if (!['active', 'disabled'].includes(status)) {
    throw new Error('Status must be "active" or "disabled"');
  }

  await updateDoc(userRef, {
    status,
    isActive: status === 'active', // Keep in sync with Firestore rules (isActiveUser check)
    updatedAt: toTimestamp(),
    updatedBy: currentUserId,
  });

  return { success: true, userId };
};

export const deleteUser = async (userId) => {
  const db = getDb();
  const userRef = doc(db, PATHS.user(userId));
  await deleteDoc(userRef);
  return true;
};

/**
 * Self-service Settings update. Writes ONLY safe personal fields (name +
 * preferences) and never role, tenantId, isActive, specialtyIds, or status — so
 * it satisfies the self-update Firestore rule (those four fields stay frozen).
 * Used by the Settings page. The caller passes the COMPLETE preferences object
 * (updateDoc replaces the preferences map, so partial objects would drop keys).
 *
 * @param {string} userId
 * @param {{ name?: string, preferences?: { theme?: 'light'|'dark', notifications?: { referralPings?: boolean } } }} fields
 */
export const updateUserSettings = async (userId, { name, preferences } = {}) => {
  if (!userId) throw new Error('updateUserSettings: userId is required');
  const db = getDb();
  const userRef = doc(db, PATHS.user(userId));

  const update = {
    updatedAt: toTimestamp(),
    settingsUpdatedAt: toTimestamp(),
  };

  if (typeof name === 'string') {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Name cannot be empty.');
    update.name = trimmed;
  }

  if (preferences && typeof preferences === 'object') {
    const prefs = {};
    if (preferences.theme === 'light' || preferences.theme === 'dark') {
      prefs.theme = preferences.theme;
    } else if (preferences.theme !== undefined) {
      throw new Error('Theme must be "light" or "dark".');
    }
    if (preferences.notifications && typeof preferences.notifications === 'object'
        && typeof preferences.notifications.referralPings === 'boolean') {
      prefs.notifications = { referralPings: preferences.notifications.referralPings };
    }
    if (preferences.learning && typeof preferences.learning === 'object'
        && typeof preferences.learning.defaultCollapsed === 'boolean') {
      prefs.learning = { defaultCollapsed: preferences.learning.defaultCollapsed };
    }
    update.preferences = prefs;
  }

  await updateDoc(userRef, update);
  return { success: true, userId };
};

// ============================================================================
// METRICS (Non-PHI only)
// Path: tenants/{tenantId}/metrics/{metricId}
// ============================================================================

// Allowed metric fields (non-PHI only)
// Required: tenantId, specialtyId, packId, versionId, submittedById
const ALLOWED_METRIC_FIELDS = [
  'tenantId',
  'specialtyId',      // Required for specialty analytics
  'packId',           // Required for pack analytics
  'versionId',        // Required for version tracking
  'outcome',
  'completenessScore',
  'redFlagsTriggeredCount',
  'triggeredRedFlagIds', // NEW: Array of which red flags were triggered
  'missingFieldIds',
  'telehealthType',
  'telehealthState',
  'submittedById',    // Required for "my submissions"
  'submittedByRole',
  'attemptNumber',    // For re-run tracking (Not Yet → Ready conversion)
  'previousEventId',  // Link to previous attempt
  'timestamp',
  'eventId',
  'savedAt',
];

// Required fields that must be non-empty strings
const REQUIRED_METRIC_FIELDS = ['tenantId', 'specialtyId', 'packId', 'versionId', 'submittedById', 'outcome'];

const sanitizeMetric = (metric) => {
  const sanitized = {};
  ALLOWED_METRIC_FIELDS.forEach((field) => {
    if (metric[field] !== undefined) {
      sanitized[field] = metric[field];
    }
  });
  return sanitized;
};

/**
 * Validate that all required fields are non-empty strings
 * @param {Object} metric
 * @throws {Error} if any required field is missing or empty
 */
const validateRequiredFields = (metric) => {
  for (const field of REQUIRED_METRIC_FIELDS) {
    const value = metric[field];
    if (!value || typeof value !== 'string' || value.trim() === '') {
      throw new Error(`saveMetricEvent: ${field} is required and must be a non-empty string`);
    }
  }
};

/**
 * Save a metric event (clinicians only)
 *
 * Required fields (all must be non-empty strings):
 *   - tenantId
 *   - specialtyId
 *   - packId
 *   - versionId
 *   - submittedById
 *   - outcome
 *
 * @param {string} tenantId - The tenant ID (also validated in metric)
 * @param {Object} metricEvent - The metric event data
 * @returns {Promise<{success: boolean, eventId: string}>}
 * @throws {Error} If required fields are missing or empty
 */
export const saveMetricEvent = async (tenantId, metricEvent) => {
  const db = getDb();
  const eventId = `MTR-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`.toUpperCase();
  const metricRef = doc(db, PATHS.metricEvent(tenantId, eventId));

  // Sanitize to allowed fields only
  const sanitized = sanitizeMetric(metricEvent);

  // Ensure tenantId matches
  sanitized.tenantId = tenantId;

  // Validate ALL required fields are present and non-empty
  validateRequiredFields(sanitized);

  // Add event metadata
  const finalMetric = {
    ...sanitized,
    eventId,
    savedAt: toTimestamp(),
  };

  await setDoc(metricRef, finalMetric);

  return { success: true, eventId };
};

/**
 * Get all metrics for a tenant (tenant_admin) with pagination
 * @param {string} tenantId
 * @param {Object} options - Pagination options
 * @param {number} options.pageSize - Number of rows per page (default 50)
 * @param {DocumentSnapshot} options.startAfter - Cursor for next page
 * @param {string} options.outcome - Filter by outcome (optional)
 * @param {string} options.specialtyId - Filter by specialty (optional)
 * @returns {Promise<{rows: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean, total: number}>}
 */
export const getMetrics = async (tenantId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcome = null, specialtyId = null } = options;

  const metricsRef = collection(db, PATHS.col.metrics(tenantId));

  // Build query with filters
  let constraints = [orderBy('savedAt', 'desc')];

  if (outcome) {
    constraints.push(where('outcome', '==', outcome));
  }
  if (specialtyId) {
    constraints.push(where('specialtyId', '==', specialtyId));
  }

  // Add cursor if paginating
  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }

  // Request one extra to check if there are more
  constraints.push(limit(pageSize + 1));

  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);

  // CRITICAL FIX: Use snapshot.docs array, not forEach with index
  const docs = snapshot.docs.slice(0, pageSize);
  const rows = [];

  docs.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) {
      data._docRef = docSnap; // Keep reference for pagination
      rows.push(data);
    }
  });

  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = snapshot.size > pageSize;

  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics for a specific specialty (specialist) with pagination
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {Object} options - Pagination options
 * @returns {Promise<{rows: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getMetricsBySpecialty = async (tenantId, specialtyId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcome = null } = options;

  const metricsRef = collection(db, PATHS.col.metrics(tenantId));

  let constraints = [
    where('specialtyId', '==', specialtyId),
    orderBy('savedAt', 'desc'),
  ];

  if (outcome) {
    constraints.push(where('outcome', '==', outcome));
  }

  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }

  constraints.push(limit(pageSize + 1));

  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);

  // CRITICAL FIX: Use snapshot.docs array, not forEach with index
  const docs = snapshot.docs.slice(0, pageSize);
  const rows = [];

  docs.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) {
      data._docRef = docSnap;
      rows.push(data);
    }
  });

  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = snapshot.size > pageSize;

  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics for a specific user (gp_employee: "my submissions") with pagination
 * @param {string} tenantId
 * @param {string|object} userId - User ID string or object with uid/id property
 * @param {Object} options - Pagination options
 * @returns {Promise<{rows: Array, rowCount: number, lastDoc: DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getMetricsByUser = async (tenantId, userId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcome = null } = options;

  // Normalize userId to string
  const uid = typeof userId === 'string' ? userId : (userId?.uid || userId?.id);

  if (!uid) {
    console.error('[getMetricsByUser] Invalid userId:', userId);
    return { rows: [], rowCount: 0, lastDoc: null, hasMore: false };
  }

  console.log('[getMetricsByUser] Query params:', {
    tenantId,
    uid,
    outcome,
    pageSize,
  });

  const metricsRef = collection(db, PATHS.col.metrics(tenantId));

  let constraints = [
    where('createdByUid', '==', uid),
    orderBy('savedAt', 'desc'),
  ];

  if (outcome) {
    constraints.push(where('outcomeStatus', '==', outcome));
  }

  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }

  constraints.push(limit(pageSize + 1));

  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);

  console.log('[getMetricsByUser] Query results:', {
    snapshotSize: snapshot.size,
    firstDocId: snapshot.docs[0]?.id || 'none',
  });

  // CRITICAL FIX: Use snapshot.docs array, not forEach with index
  // Firestore QuerySnapshot.forEach does NOT provide an index parameter
  const docs = snapshot.docs.slice(0, pageSize);
  const rows = [];

  docs.forEach((docSnap) => {
    const data = cleanDoc(docSnap);

    if (data) {
      data._docRef = docSnap;
      rows.push(data);
    } else {
      console.warn('[getMetricsByUser] cleanDoc returned null for doc:', docSnap.id);
    }
  });

  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = snapshot.size > pageSize;

  console.log('[getMetricsByUser] Returning:', {
    rowCount: rows.length,
    hasMore,
    sampleRow: rows[0] ? {
      id: rows[0].id,
      outcomeStatus: rows[0].outcomeStatus,
      savedAtMs: rows[0].savedAtMs,
    } : null,
  });

  return { rows, rowCount: rows.length, lastDoc, hasMore };
};

// ============================================================================
// AUDIT LOGS
// Path: tenants/{tenantId}/audit_logs/{runId}
//
// Contains decision traces for compliance and review
// Does NOT contain PHI - only decision metadata and trace
//
// Access control:
//   - specialist: own specialty's logs (write + read)
//   - gp_employee: own submissions only (write + read own)
//   - tenant_admin: all tenant logs (read-only)
//   - platform_admin: BLOCKED (separation of concerns)
// ============================================================================

/**
 * Save an audit log entry
 * @param {string} tenantId
 * @param {Object} auditLog - The audit log entry from createAuditLog
 * @returns {Promise<{success: boolean, runId: string}>}
 */
export const saveAuditLog = async (tenantId, auditLog) => {
  const db = getDb();
  const runId = auditLog.runId;

  if (!runId) {
    throw new Error('saveAuditLog: auditLog.runId is required');
  }

  const auditRef = doc(db, PATHS.auditLogFlat(tenantId, runId));

  const finalLog = {
    ...auditLog,
    savedAt: toTimestamp(),
  };

  await setDoc(auditRef, finalLog);

  return { success: true, runId };
};

/**
 * Get audit logs for a specialty (specialist view)
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {number} limitCount
 * @returns {Promise<Array>}
 */
export const getAuditLogsBySpecialty = async (tenantId, specialtyId, limitCount = 100) => {
  const db = getDb();
  const logsRef = collection(db, PATHS.col.auditLogsFlat(tenantId));
  const q = query(
    logsRef,
    where('specialtyId', '==', specialtyId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);

  const logs = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) logs.push(data);
  });

  return logs;
};

/**
 * Get audit logs for a user (gp_employee "my decisions" view)
 * @param {string} tenantId
 * @param {string} userId
 * @param {number} limitCount
 * @returns {Promise<Array>}
 */
export const getAuditLogsByUser = async (tenantId, userId, limitCount = 100) => {
  const db = getDb();
  const logsRef = collection(db, PATHS.col.auditLogsFlat(tenantId));
  const q = query(
    logsRef,
    where('context.userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);

  const logs = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) logs.push(data);
  });

  return logs;
};

/**
 * Get all audit logs for a tenant (tenant_admin view)
 * @param {string} tenantId
 * @param {number} limitCount
 * @returns {Promise<Array>}
 */
export const getAuditLogs = async (tenantId, limitCount = 200) => {
  const db = getDb();
  const logsRef = collection(db, PATHS.col.auditLogsFlat(tenantId));
  const q = query(
    logsRef,
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);

  const logs = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) logs.push(data);
  });

  return logs;
};

/**
 * Get a single audit log by runId
 * @param {string} tenantId
 * @param {string} runId
 * @returns {Promise<Object|null>}
 */
export const getAuditLog = async (tenantId, runId) => {
  const db = getDb();
  const logRef = doc(db, PATHS.auditLogFlat(tenantId, runId));
  const docSnap = await getDoc(logRef);

  if (!docSnap.exists()) return null;
  return cleanDoc(docSnap);
};

// ============================================================================
// SEED TEMPLATES
// Path: seed_templates/specialties/{specialtyId}
// Path: seed_templates/packs/{specialtyId}__{packId}__{versionId}
// ============================================================================

export const getSeedSpecialties = async () => {
  const db = getDb();
  const ref = collection(db, PATHS.col.seedSpecialties());
  const snapshot = await getDocs(ref);

  const specialties = {};
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) specialties[docSnap.id] = data;
  });

  return specialties;
};

export const getSeedPacks = async () => {
  const db = getDb();
  const ref = collection(db, PATHS.col.seedPacks());
  const snapshot = await getDocs(ref);

  const packs = {};
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) packs[docSnap.id] = data;
  });

  return packs;
};

export const saveSeedSpecialty = async (specialty) => {
  const db = getDb();
  const specialtyId = specialty.specialtyId;
  const ref = doc(db, PATHS.seedSpecialtyDoc(specialtyId));

  await setDoc(ref, {
    ...specialty,
    createdAt: toTimestamp(),
    updatedAt: toTimestamp(),
  });

  return { success: true };
};

export const saveSeedPack = async (specialtyId, packId, versionId, packData) => {
  const db = getDb();
  const templateId = `${specialtyId}__${packId}__${versionId}`;
  const ref = doc(db, PATHS.seedPackDoc(templateId));

  await setDoc(ref, {
    specialtyId,
    packId,
    versionId,
    name: packData.metadata?.name || packId,
    status: 'published',
    schemaVersion: 1,
    pack: packData,
    createdAt: toTimestamp(),
    updatedAt: toTimestamp(),
  });

  return { success: true };
};

// ============================================================================
// SEED TENANT (Copy templates to tenant)
// ============================================================================

export const seedTenant = async (tenantId) => {
  const db = getDb();
  const batch = writeBatch(db);
  const userId = getCurrentUserId();

  // Get seed templates
  const seedSpecialties = await getSeedSpecialties();
  const seedPacks = await getSeedPacks();

  // Copy specialties
  for (const [specId, specData] of Object.entries(seedSpecialties)) {
    const specRef = doc(db, PATHS.specialty(tenantId, specId));

    // Find the default active pack for this specialty
    let activePack = null;
    for (const [packTemplateId, packData] of Object.entries(seedPacks)) {
      if (packData.specialtyId === specId && packData.status === 'published') {
        activePack = { packId: packData.packId, versionId: packData.versionId };
        break;
      }
    }

    batch.set(specRef, {
      specialtyId: specId,
      name: specData.name,
      description: specData.description || '',
      icon: specData.icon || 'Shield',
      status: 'active',
      sortOrder: specData.sortOrder || 0,
      activePack,
      createdAt: toTimestamp(),
      createdBy: userId,
      updatedAt: toTimestamp(),
      updatedBy: userId,
    });
  }

  // Copy packs
  for (const [templateId, packData] of Object.entries(seedPacks)) {
    const { specialtyId, packId, versionId } = packData;
    const packDocId = `${packId}__${versionId}`;
    const packRef = doc(db, PATHS.pack(tenantId, specialtyId, packDocId));

    batch.set(packRef, {
      tenantId,
      specialtyId,
      packId,
      versionId,
      name: packData.name,
      status: 'published',
      tags: packData.tags || [],
      description: packData.pack?.metadata?.intent || '',
      schemaVersion: packData.schemaVersion || 1,
      pack: packData.pack,
      createdAt: toTimestamp(),
      createdBy: userId,
      updatedAt: toTimestamp(),
      updatedBy: userId,
    });
  }

  await batch.commit();

  // Update tenant counters
  await updateTenantCounters(tenantId);

  return { success: true, specialtiesCount: Object.keys(seedSpecialties).length, packsCount: Object.keys(seedPacks).length };
};

// ============================================================================
// PLATFORM STATS (For Platform Admin Dashboard)
// ============================================================================

export const getPlatformStats = async () => {
  const db = getDb();

  // Count tenants
  const tenantsRef = collection(db, PATHS.col.tenants());
  const activeTenantsQuery = query(tenantsRef, where('status', '==', 'active'));
  const [allTenantsSnap, activeTenantsSnap] = await Promise.all([
    getDocs(tenantsRef),
    getDocs(activeTenantsQuery),
  ]);

  // Count users
  const usersRef = collection(db, PATHS.col.users());
  const activeUsersQuery = query(usersRef, where('status', '==', 'active'));
  const [allUsersSnap, activeUsersSnap] = await Promise.all([
    getDocs(usersRef),
    getDocs(activeUsersQuery),
  ]);

  return {
    tenants: {
      total: allTenantsSnap.size,
      active: activeTenantsSnap.size,
    },
    users: {
      total: allUsersSnap.size,
      active: activeUsersSnap.size,
    },
  };
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Tenants
  getTenants,
  getTenant,
  saveTenant,
  updateTenantLifecycle,
  deleteTenant,
  updateTenantCounters,

  // Specialties
  getSpecialties,
  getSpecialty,
  saveSpecialty,
  deleteSpecialty,

  // Tenant Settings
  getTenantSettings,
  getSecuritySettings,
  saveTenantSettings,
  saveSecuritySettings,

  // Packs
  getPacks,
  getPackByDocId,
  getPackVersions,
  savePackVersion,
  updatePackStatus,
  updatePackDoc,
  setActivePack,
  deletePack,
  isCustomPack,

  // Users
  getUsers,
  getTenantUsers,
  getUser,
  getTenantUser,
  saveUser,
  deleteUser,

  // Metrics
  saveMetricEvent,
  getMetrics,
  getMetricsBySpecialty,
  getMetricsByUser,

  // Audit Logs
  saveAuditLog,
  getAuditLogs,
  getAuditLogsBySpecialty,
  getAuditLogsByUser,
  getAuditLog,

  // Seed
  getSeedSpecialties,
  getSeedPacks,
  saveSeedSpecialty,
  saveSeedPack,
  seedTenant,

  // Platform Stats
  getPlatformStats,
};
